import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { motion } from "framer-motion";

export default function ContactForm() {
  const neonGradientText = {
    background: "linear-gradient(90deg, #00F5A0, #00CFFF)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    projectType: "",
    projectInfo: "",
    phoneNumber: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Frontend validation
    if (!formData.name || !formData.email || !formData.subject || !formData.projectType || !formData.projectInfo) {
      return alert("Please fill all required fields");
    }

    try {
      const res = await fetch("http://localhost:5000/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      alert(data.message);

      if (data.success) {
        setFormData({
          name: "",
          email: "",
          subject: "",
          projectType: "",
          projectInfo: "",
          phoneNumber: "",
        });
      }
    } catch (error) {
      alert("Something went wrong");
    }
  };

const GlobalStyles = `/* ---------------- Scrollbar ---------------- */
::-webkit-scrollbar {
  width: 8px;
}
::-webkit-scrollbar-track {
  background: #010409;
}
::-webkit-scrollbar-thumb {
  background: #00F5A0;
  border-radius: 10px;
}

/* ---------------- Inputs, Textarea, Select ---------------- */
.input-field,
textarea,
select {
  padding: 16px;
  width: 100%;
  background: rgba(11, 14, 17, 0.9); /* MATCH THEME */
  color: #FFFFFF;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  outline: none;
  transition: all 0.4s ease;
}

/* Glow on focus */
.input-field:focus,
textarea:focus,
select:focus {
  border: 1px solid #00F5A0;
  background: rgba(11, 14, 17, 0.9); /* keep same theme background */
  box-shadow: 0 0 20px rgba(0, 245, 160, 0.15);
}

/* ---------------- Floating Labels ---------------- */
.floating-label {
  position: absolute;
  left: 16px;
  top: 16px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.4);
  pointer-events: none;
  transition: all 0.3s ease;
}

/* Move label up for input/textarea when focused or filled */
.input-field:focus + .floating-label,
.input-field:not(:placeholder-shown) + .floating-label,
textarea:focus + .floating-label,
textarea:not(:placeholder-shown) + .floating-label {
  top: -10px;
  left: 12px;
  font-size: 12px;
  color: #00F5A0;
  background: rgb(0, 0, 0); /* MATCH THEME */
  padding: 0 8px;
  border-radius: 4px;
}

/* Move label up for select only when a value is chosen */
select:valid + .floating-label {
  top: -10px;
  left: 12px;
  font-size: 12px;
  color: #00F5A0;
  background: rgb(0, 0, 0); /* MATCH THEME */
  padding: 0 8px;
  border-radius: 4px;
}

/* ---------------- Button ---------------- */
.cta-button {
  background: linear-gradient(90deg, #00F5A0, #00CFFF);
  color: #000;
  transition: all 0.4s ease;
}

.cta-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(0, 245, 160, 0.4);
}

/* ---------------- Neon Scroll for Select ---------------- */
.neon-scroll::-webkit-scrollbar {
  width: 10px;
}
.neon-scroll::-webkit-scrollbar-track {
  background: rgba(1, 4, 9, 0.8);
  border-radius: 12px;
}
.neon-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #00F5A0, #00CFFF);
  border-radius: 12px;
  box-shadow: 0 0 8px #00F5A0, 0 0 12px #00CFFF;
  transition: all 0.3s ease;
}
.neon-scroll::-webkit-scrollbar-thumb:hover {
  box-shadow: 0 0 12px #00F5A0, 0 0 20px #00CFFF;
  transform: scale(1.05);
}

.neon-scroll {
  scroll-behavior: smooth;
  max-height: 180px;
  overflow-y: auto;
  padding-right: 6px;
}

.neon-scroll option {
  background: rgba(11, 14, 17, 0.9); /* MATCH THEME */
  color: #fff;
  padding: 10px 16px;
}

.neon-scroll:focus {
  border: 1px solid #00F5A0;
  box-shadow: 0 0 20px rgba(0, 245, 160, 0.3), 0 0 30px rgba(0, 207, 255, 0.2);
}

`;



  return (
    <div className="min-h-screen text-white flex flex-col bg-[#0B0E11] overflow-hidden">
      <style>{GlobalStyles}</style>
      <Navbar />

      <section className="relative flex-1 py-24 px-4 sm:px-6 lg:px-24 flex items-center justify-center">
        <div className="max-w-5xl w-full grid grid-cols-1 lg:grid-cols-12 gap-16 relative z-10">
          {/* Left Side: Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="lg:col-span-5 space-y-8"
          >
            <div>
              <h1 className="text-5xl md:text-6xl font-black leading-tight">
                GET <br />
                <span style={neonGradientText}>IN TOUCH</span>
              </h1>
              <div className="w-16 h-1 bg-gradient-to-r from-[#00F5A0] to-[#00CFFF] mt-6 rounded-full" />
            </div>
            <p className="text-white/50 text-lg font-light leading-relaxed">
              Have a project in mind? Let’s collaborate to build something that stands out in the digital landscape.
            </p>
          </motion.div>

          {/* Right Side: Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="lg:col-span-7"
          >
     <form
  onSubmit={handleSubmit}
  className="p-8 md:p-10 rounded-[2.5rem] space-y-6 relative border border-white/10 form-neon"
>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder=" "
                    className="input-field"
                    value={formData.name}
                    onChange={handleChange}
                  />
                  <label className="floating-label">Name</label>
                </div>

                {/* Email */}
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder=" "
                    className="input-field"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  <label className="floating-label">Email</label>
                </div>
              </div>

              {/* Subject */}
              <div className="relative">
                <input
                  type="text"
                  name="subject"
                  required
                  placeholder=" "
                  className="input-field"
                  value={formData.subject}
                  onChange={handleChange}
                />
                <label className="floating-label">Subject</label>
              </div>

              {/* Project Type */}
<div className="relative">
  <select
    name="projectType"
    required
    className="input-field neon-scroll"
    value={formData.projectType}
    onChange={handleChange}
  >
    <option value="" disabled>Select Project Type</option> {/* Empty value */}
    <option value="Website Project">Website Project</option>
    <option value="Mobile App">Mobile App</option>
    <option value="E-commerce Platform">E-commerce Platform</option>
    <option value="Branding / Design">Branding / Design</option>
    <option value="Marketing / SEO">Marketing / SEO</option>
    <option value="Other">Other</option>
  </select>
  <label className="floating-label">Project Type</label>
</div>


<style>

</style>

              {/* Project Info */}
              <div className="relative">
                <textarea
                  rows={4}
                  name="projectInfo"
                  required
                  placeholder=" "
                  className="input-field"
                  value={formData.projectInfo}
                  onChange={handleChange}
                />
                <label className="floating-label">Tell us about your project</label>
              </div>

              {/* Phone Number (Optional) */}
              <div className="relative">
                <input
                  type="text"
                  name="phoneNumber"
                  placeholder=" "
                  className="input-field"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
                <label className="floating-label">Phone (Optional)</label>
              </div>

              <button
                type="submit"
                className="cta-button w-full py-4 rounded-xl font-black text-sm uppercase tracking-[0.2em] shadow-lg"
              >
                Send Proposal
              </button>
            </form>
           <style>

</style>

          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
